import axios from "axios";
import React, { useEffect, useState } from "react";
import { api } from "../utils/Api";
import {
	Typography,
	Flex,
	Button,
	Row,
	Col,
	DatePicker,
	Select,
	Form,
	Input,
	notification,
	Modal,
} from "antd";
import { CustomSpinner, CustomTable } from "../utils/CustomComponents";
import dayjs from "dayjs";
import JobPerformanceLoads from "./JobPerformanceLoads";
import { useRef } from "react";
const { RangePicker } = DatePicker;

const { Text } = Typography;

const JobPerformanceBody = ({ hasClient = false, customerId }) => {
	const [loading, setLoading] = useState(true);
	const [loadingTable, setLoadingTable] = useState(false);
	const [plants, setPlants] = useState([]);
	const [customers, setCustomers] = useState([]);
	const [foremans, setForemans] = useState([]);
	const [jobList, setJobList] = useState([]);
	const [disabled, setDisabled] = useState(true);
	const job = useRef(null);
	const [openLoadsModal, setOpenLoadsModal] = useState(false);

	const [form] = Form.useForm();

	const [apiNotification, contextHolder] = notification.useNotification();

	const openNotification = (type, message, description) => {
		apiNotification[type]({
			message: message,
			description: description,
		});
	};

	/**
	 ** Options header (columns)
	 */
	const columns = [
		{
			title: "Date",
			dataIndex: "date",
			key: "date",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Job ID",
			dataIndex: "jobId",
			key: "jobId",
			render: (value, record) => {
				return <Text strong>{value}</Text>;
			},
		},
		{
			title: "Client",
			dataIndex: "client",
			key: "client",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Seller",
			dataIndex: "seller",
			key: "seller",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "PSI",
			dataIndex: "psi",
			key: "psi",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Plant",
			dataIndex: "plant",
			key: "plant",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Foreman",
			dataIndex: "foreman",
			key: "foreman",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Action",
			dataIndex: "action",
			key: "action",
			render: (value, record) => {
				return (
					<Button
						tton
						type="primary"
						onClick={() => {
							job.current = record;
							setOpenLoadsModal(true);
						}}
					>
						Loads
					</Button>
				);
			},
		},
	];

	useEffect(() => {
		getValues();
	}, []);

	useEffect(() => {
		if (!customerId) return;

		form.setFieldsValue({ customer: customerId });
		getJobs({
			...form.getFieldsValue(),
			customer: customerId,
		});
	}, [customerId]);

	const getValues = async () => {
		await Promise.all([getPlants(), getCustomers(), getForemans()]).then(() => {
			setLoading(false);
			setDisabled(false);
		});
	};

	const getPlants = async () => {
		const url = api + "/plant/list/57Concrete";
		const token = localStorage.getItem("token");
		axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

		try {
			const { data } = await axios.get(url);
			let list = [];
			data.payload.map((item) => {
				list.push({
					value: item._id,
					label: item.name,
				});
			});
			setPlants(list);
		} catch (err) {
			openNotification("error", "Error", "Error while trying to get plants");
			console.error(err.message);
			return null;
		}
	};

	const getCustomers = async () => {
		try {
			const url = api + "/customer/list/options/57Concrete";
			const token = localStorage.getItem("token");
			axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

			const { data } = await axios.get(url);
			if (data.success) {
				let list = [];
				data.payload.map((item) => {
					list.push({
						label: `${item.customerNumber} ${item.name}`,
						value: item._id,
					});
				});
				setCustomers(list);
			} else {
				openNotification("error", "Error", data.message);
			}
		} catch (err) {
			openNotification("error", "Error", "Error while trying to get customers");
			console.error(err.message);
			return null;
		}
	};

	const getForemans = async () => {
		try {
			const url = api + "/foreman/list/57Concrete";
			const token = localStorage.getItem("token");
			axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

			const { data } = await axios.get(url);
			let list = [];
			data.map((item) => {
				list.push({
					label: `${item.firstName} ${item.lastName}`,
					value: item._id,
				});
			});
			setForemans(list);
		} catch (err) {
			openNotification("error", "Error", "Error while trying to get foremans");
			console.error(err.message);
			return null;
		}
	};

	const getJobs = async (values) => {
		try {
			setLoadingTable(true);
			setDisabled(true);

			const { dates, customer, foreman, plant, jobId } = values;

			const formattedDateStart = dayjs(dates[0]);
			const start = `${formattedDateStart.year()}-${
				formattedDateStart.month() + 1
			}-${formattedDateStart.date()}`;
			const formattedDateEnd = dayjs(dates[1]);
			const end = `${formattedDateEnd.year()}-${
				formattedDateEnd.month() + 1
			}-${formattedDateEnd.date()}`;

			const url =
				api +
				`/job/list/performance?startDate=${start}&endDate=${end}&plant=${plant}&customer=${customer}&foreman=${foreman}&jobId=${jobId}`;

			const token = localStorage.getItem("token");
			axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

			const { data } = await axios.get(url);

			if (data.success) {
				const payload = data.payload;
				setJobList(
					payload.map((item) => ({
						key: item._id,
						date: dayjs(item.scheduledDate).format("MM/DD/YYYY"),
						dateTime: dayjs(item.scheduledDate).format("HH:mm A"),
						jobId: item.number,
						client: item.customer?.name || "N/A",
						seller: item.seller?.name || "N/A",
						psi: item.psiId ? item.psiId.psi : item.psi,
						plant: item.plant?.name || "N/A",
						foreman: `${item.foreman?.firstName} ${item.foreman?.lastName}`,
					})),
				);
			} else {
				openNotification("error", "Error", data.message);
			}

			setLoadingTable(false);
			setDisabled(false);
		} catch (err) {
			console.error(err.message);
			return null;
		}
	};

	const validateFields = (_, value) => {
		const values = form.getFieldsValue();
		const filledFields = Object.values(values).filter(
			(v) => v !== undefined && v !== "" && v !== null,
		);
		if (filledFields.length === 1) {
			openNotification(
				"warning",
				"Validation Error",
				"At least one field must be filled.",
			);
			return Promise.reject(new Error("At least one field must be filled."));
		}
		return Promise.resolve();
	};

	const onFinish = (values) => {
		delete values.validation;
		getJobs(values);
	};

	const filterOption = (input, option) =>
		(option?.label ?? "").toLowerCase().includes(input.toLowerCase());

	const RenderHeader = () => {
		return (
			<Form
				form={form}
				layout="vertical"
				onFinish={onFinish}
				initialValues={{
					dates: [dayjs(), dayjs()],
					customer: customerId,
				}}
			>
				<Row gutter={16} align={"middle"} justify={"start"}>
					<Col span={24} sm={12} xl={6}>
						<Form.Item name="dates">
							<RangePicker
								size="large"
								allowClear={false}
								format={"MM/DD/YYYY"}
								style={{ width: "100%" }}
								disabled={disabled}
							/>
						</Form.Item>
					</Col>
					<Col span={24} sm={12} xl={6}>
						<Form.Item name="customer">
							<Select
								size="large"
								options={customers}
								style={{ width: "100%" }}
								loading={disabled}
								disabled={disabled || hasClient}
								allowClear
								placeholder="Select Customer"
								filterOption={filterOption}
								showSearch
							/>
						</Form.Item>
					</Col>
					{!hasClient && (
						<Col span={24} sm={12} xl={6}>
							<Form.Item name="foreman">
								<Select
									size="large"
									options={foremans}
									style={{ width: "100%" }}
									loading={disabled}
									disabled={disabled}
									allowClear
									placeholder="Select Foreman"
									filterOption={filterOption}
									showSearch
								/>
							</Form.Item>
						</Col>
					)}
					<Col span={24} sm={12} xl={6}>
						<Form.Item name="plant">
							<Select
								size="large"
								options={plants}
								style={{ width: "100%" }}
								loading={disabled}
								disabled={disabled}
								allowClear
								placeholder="Select Plant"
								filterOption={filterOption}
								showSearch
							/>
						</Form.Item>
					</Col>
					<Col span={24} sm={12} xl={6}>
						<Form.Item name="jobId">
							<Input
								size="large"
								style={{ width: "100%" }}
								placeholder="Job ID"
								disabled={disabled}
							/>
						</Form.Item>
					</Col>
					{/* Hidden validation trigger */}
					<Form.Item
						name="validation"
						noStyle
						dependencies={["customer", "plant", "foreman", "jobId"]}
						rules={[{ validator: validateFields }]}
					>
						<Input type="hidden" />
					</Form.Item>
					<Col span={24} sm={12} xl={6}>
						<Form.Item>
							<Button
								type="primary"
								htmlType="submit"
								size="large"
								style={{ width: "100%" }}
								disabled={disabled}
							>
								Filter
							</Button>
						</Form.Item>
					</Col>
				</Row>
			</Form>
		);
	};

	const RenderTable = () => {
		return (
			<Flex gap={"middle"} vertical>
				<CustomTable
					columns={columns}
					data={jobList}
					loading={loadingTable}
					isScroll={true}
				/>
			</Flex>
		);
	};

	const RenderModal = () => {
		return (
			<Modal
				title={`Job ID: ${job.current?.jobId}`}
				open={openLoadsModal}
				onCancel={() => {
					setOpenLoadsModal(false);
					job.current = null;
				}}
				footer={null}
				width={1200}
				destroyOnClose
				centered
			>
				<JobPerformanceLoads job={job.current} />
			</Modal>
		);
	};

	return (
		<>
			<Flex vertical gap={"large"}>
				<RenderHeader />
				{loading ? <CustomSpinner /> : <RenderTable />}
				{contextHolder}
				<RenderModal />
			</Flex>
		</>
	);
};

export default JobPerformanceBody;
